import "./index.scss";

import { Box, Grid, Stack } from "@mui/material";
import { ReactComponent as IconSortVertical } from "assets/images/icons/moving-vertical.svg";
import { ReactComponent as IconSelectorCornerNormal } from "assets/images/icons/selector-corner-normal.svg";
import { ReactComponent as IconSelectorCornerNormalHover } from "assets/images/icons/selector-corner-normal-hover.svg";
import { ReactComponent as IconSelectorCornerSelected } from "assets/images/icons/selector-corner-selected.svg";
import { ReactComponent as IconSelectorCornerSelectedHover } from "assets/images/icons/selector-corner-selected-hover.svg";
import { ReactComponent as IconTrash } from "assets/images/icons/trash.svg";
import { ReactComponent as IconEdit } from "assets/images/square-buttons/edit-line.svg";
import Button from "components/Button";
import CheckBox from "components/Checkbox";
import ScreenEmptySection from "components/ScreenEmptySection";
import _ from "lodash";
import { CardioProgram } from "models/workoutplan";
import { nanoid } from "nanoid";
import { memo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";

import ProgramsExercisesCreateEdit from "../ProgramsExercisesCreateEdit";

const ProgramsExercisesGrid = (props: { programs: CardioProgram[] }) => {
  const { programs } = props;
  const formContext = useFormContext();
  const { setValue } = formContext;
  const [selectedProgram, setSelectedProgram] = useState<string>();
  const [programsList, setProgramsList] = useState<CardioProgram[]>([]);
  const [exercisesList, setExercisesList] = useState(programs[0] && [...programs[0]?.exercises]);
  const [showProgramsExercisesCreateEditModal, setShowProgramsExercisesCreateEditModal] =
    useState(false);
  const [isCreateProgramNotEdit, setIsCreateProgramNotEdit] = useState(false);

  useEffect(() => {
    if (programs?.length) {
      let tempList = programs.map((m) => {
        if (!m.nanoId) {
          m.nanoId = nanoid();
        }
        return m;
      });
      setProgramsList(tempList);
      setSelectedProgram(tempList[0].nanoId);
    }
  }, [programs]);

  useEffect(() => {
    //Update program array after sorting
    if (selectedProgram && exercisesList) {
      let tempList = [...programsList];
      let entry = tempList.find((f) => f.nanoId === selectedProgram);
      entry.exercises = [...exercisesList];
      setProgramsList(tempList);
      setValue("cardio_workouts", tempList);
    }
  }, [exercisesList]);

  //#region event handlers
  const handleProgramsExercisesCreateEditReturn = (value: CardioProgram) => {
    if (value) {
      let newList = [...programsList];
      if (!isCreateProgramNotEdit) {
        let entryToEdit = newList.findIndex((f) => f.nanoId === value.nanoId);
        newList[entryToEdit] = _.cloneDeep(value);
      } else {
        newList = [
          ...programsList,
          { ...value, is_active: 1, is_express: programsList.length > 0 ? 0 : 1 },
        ];
      }
      setProgramsList(newList);
      setSelectedProgram(value.nanoId);
      setExercisesList(value.exercises);
      setValue("cardio_workouts", newList);
    }
    setShowProgramsExercisesCreateEditModal(false);
  };

  const handleRemoveExercise = (itemId) => {
    let copy = [...exercisesList];
    const indexToDelete = copy.findIndex((e) => e.id === itemId);
    copy.splice(indexToDelete, 1);
    setExercisesList(copy);
  };

  const handleSetActive = (nanoId, value) => {
    let newList = [...programsList];
    let entryToEdit = newList.find((f) => f.nanoId === nanoId);
    entryToEdit.is_active = value ? 1 : 0;
  };

  const handleSetExpress = (nanoId, value) => {
    let newList = [...programsList];
    let entryToEdit = newList.find((f) => f.nanoId === nanoId);
    if (value) {
      let oldExpressEntry = newList.find((e) => e.is_express === 1);
      if (oldExpressEntry) oldExpressEntry.is_express = 0;
    }
    entryToEdit.is_express = value ? 1 : 0;
  };
  //#endregion event handlers

  return (
    <>
      <Grid className="program-exercise-grid">
        <Stack direction="column" justifyContent="left" mt={"42px"} mb={"20px"} gap={"10px"}>
          <Box>
            <h2 className="headerSpacing">Define Programs</h2>
          </Box>
          <Button
            label="Add New Cardio Program"
            className="confirm-button"
            type="button"
            onClick={() => {
              setIsCreateProgramNotEdit(true);
              setShowProgramsExercisesCreateEditModal(true);
            }}
          />
        </Stack>
        <Box className="primary-details-section">
          <Box className="inner-container program-selection-container">
            <Grid container>
              <Grid item xs={4}>
                <Grid container>
                  <Grid item xs={7} className="header" pl="12px">
                    Programs
                  </Grid>
                  <Grid item xs={2} className="header">
                    Active
                  </Grid>
                  <Grid item xs={2} className="header">
                    Express
                  </Grid>
                  <Grid item xs={1}></Grid>
                  {programsList.map((e, index) => (
                    <Grid
                      container
                      className={`program-entry ${e.nanoId === selectedProgram ? `selected` : ``}`}
                      alignItems={"center"}
                      onClick={() => {
                        setSelectedProgram(e.nanoId);
                        setExercisesList(e.exercises);
                      }}
                      key={`program_${index}`}
                    >
                      <Grid item xs={7}>
                        {e.name}
                      </Grid>
                      <Grid item xs={2}>
                        <CheckBox
                          checked={e.is_active ? true : false}
                          onChange={(v) => handleSetActive(e.nanoId, v)}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <CheckBox
                          checked={e.is_express ? true : false}
                          onChange={(v) => handleSetExpress(e.nanoId, v)}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconSelectorCornerNormal className="showNormal" />
                        <IconSelectorCornerNormalHover className="showNormalHover" />
                        <IconSelectorCornerSelected className="showSelected" />
                        <IconSelectorCornerSelectedHover className="showSelectedHover" />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={8}>
                <Grid container>
                  <Grid item xs={6} className="header">
                    Exercises, Machines, and Classes
                  </Grid>
                  <Grid item xs={5} className="header">
                    Time (in minutes)
                  </Grid>
                  <Grid item xs={1} className="header" justifyContent={"flex-end"}>
                    {selectedProgram && (
                      <button
                        type="button"
                        className="edit-icon"
                        onClick={() => {
                          setIsCreateProgramNotEdit(false);
                          setShowProgramsExercisesCreateEditModal(true);
                        }}
                      >
                        <IconEdit />
                      </button>
                    )}
                  </Grid>
                </Grid>
                <div className="sortableAreaContainer">
                  {exercisesList && (
                    <ReactSortable list={exercisesList} setList={setExercisesList} animation={200}>
                      {exercisesList &&
                        exercisesList.map((item, index) => (
                          <Grid
                            container
                            alignItems={"center"}
                            className="exercise-entry"
                            key={`program_exercise_${index}`}
                          >
                            <Grid item xs={6}>
                              <IconSortVertical className="sort-handle" />
                              {item.name}
                            </Grid>
                            <Grid item xs={5}>
                              {item.time}
                            </Grid>
                            <Grid item xs={1}>
                              <IconTrash onClick={() => handleRemoveExercise(item.id)} />
                            </Grid>
                          </Grid>
                        ))}
                    </ReactSortable>
                  )}
                  {!exercisesList?.length && <ScreenEmptySection />}
                </div>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
      <ProgramsExercisesCreateEdit
        open={showProgramsExercisesCreateEditModal}
        onClose={handleProgramsExercisesCreateEditReturn}
        program={programsList.find((f) => f.nanoId === selectedProgram)}
        isCreate={isCreateProgramNotEdit}
      />
    </>
  );
};

export default memo(ProgramsExercisesGrid);
